import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC, useEffect, useRef, useState } from 'react';
import { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronDownIcon, SearchIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { Company, useScreenSize } from '../../../../hooks/index';
import { useStakeholdersForExercise } from '../../../../hooks/stakeholder/index';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { Stakeholder } from '../../../../types/stakeholder.types';
import { FormSchema } from '../exercise-form.validation';

export type StakeholderValue = {
  fullName: string;
  id: string;
};

export type StakeholdersItemsListProps = {
  control: Control<FormSchema>;
  data: Stakeholder[];
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          stakeholders: Stakeholder[];
          totalPages: number;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage: boolean;
  isLoading: boolean;
  name: string;
};

const StakeholdersItemsList: FC<StakeholdersItemsListProps> = ({
  data,
  control,
  name,
  hasNextPage,
  fetchNextPage,
  isLoading,
}) => {
  const { scrollableTarget } = useScreenSize();
  return (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={data.length || 0}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget={scrollableTarget}
    >
      {data.length > 0 ? (
        <>
          {data
            .sort((a, b) => a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase()))
            .map(({ fullName, id }, i) => (
              <FormComboboxOption
                className="w-full"
                control={control}
                key={`${fullName}_${i}`}
                name={name as keyof FormSchema}
                value={{ id, fullName } as unknown as FormSchema[keyof FormSchema]}
              >
                <Tooltip content={fullName}>
                  <span className="truncate text-sm text-gray-700">{fullName}</span>
                </Tooltip>
              </FormComboboxOption>
            ))}
        </>
      ) : isLoading ? (
        <span className="py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="LOADING" />
        </span>
      ) : (
        <span className="py-[6px] text-sm font-normal text-gray-700">
          <AppFormattedMessage id="NO_SHAREHOLDER_FOUND" />
        </span>
      )}
    </InfiniteScroll>
  );
};

export type StakeholderComboboxProps = {
  companyId: string;
  control: Control<FormSchema>;
  formData: UseFormGetValues<FormSchema>;
  handleStakeholderChange: () => void;
  name: string;
  planType: SharePlanType;
  setFormData: UseFormSetValue<FormSchema>;
};

export const StakeholderCombobox: FC<StakeholderComboboxProps> = ({
  control,
  companyId,
  name,
  handleStakeholderChange,
  planType,
  setFormData,
  formData,
}) => {
  const [search, setSearch] = useState<string>('');
  const { selectedCompany } = Company.useSelected();

  const { stakeholders, fetchNextPage, hasNextPage, isLoading } = useStakeholdersForExercise({
    companyId,
    search: search || '',
    planType,
    showTerminated: true,
  });

  const filteredStakeholders = stakeholders.filter(
    (stakeholder) =>
      stakeholder?.vestingTasks &&
      stakeholder?.vestingTasks?.length > 0 &&
      stakeholder.id === selectedCompany?.stakeholder?.id,
  );

  useEffect(() => {
    if (filteredStakeholders && filteredStakeholders.length === 1) {
      const { id, fullName } = filteredStakeholders[0];
      const currentStakeholder = formData(name as keyof FormSchema) as
        | { fullName: string; id: string }
        | undefined;
      if (!currentStakeholder || currentStakeholder.id !== id)
        setFormData(
          name as keyof FormSchema,
          { id, fullName } as unknown as FormSchema[keyof FormSchema],
        );
    }
  }, [filteredStakeholders, formData, name, setFormData]);

  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <FormCombobox
        className="w-full"
        control={control}
        name={name as keyof FormSchema}
        onFocus={() => setSearch('')}
        onSelect={() => {
          setSearch('');
          handleStakeholderChange();
        }}
      >
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) => (value as StakeholderValue)?.fullName}
            icon={<ChevronDownIcon />}
            name={name as keyof FormSchema}
            readOnly
            wrapperClassName="h-10"
          />
          <ComboboxButton className="absolute left-0 top-0 z-20 h-full w-full" />
        </div>
        <ComboboxOptions className="z-20 max-h-[157px] w-fit max-w-[156px] overflow-x-hidden">
          <div className="relative flex w-full items-center" onClick={() => ref.current?.focus()}>
            <SearchIcon className="absolute ml-[6px]" />
            <input
              className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
              onChange={({ target: { value } }) => setSearch(value)}
              ref={ref}
              value={search}
            />
          </div>
          <StakeholdersItemsList
            control={control}
            data={filteredStakeholders || []}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isLoading}
            name={name}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
